"use client";

import { useDeferredValue, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { JsonIndent } from "@/lib/tool-kit/latex-dev/data-model";
import {
  type AttributePrefix,
  DEFAULT_XML_JSON_OPTIONS,
  jsonToXml,
  type TextKey,
  type XmlIndent,
  type XmlJsonOptions,
  type XmlOutcome,
  type XmlProblem,
  xmlToJson,
} from "@/lib/tool-kit/latex-dev/xml-json";

type Direction = "xmlToJson" | "jsonToXml";

/** English strings. A translated content file overrides these through `ui`. */
export const XML_TO_JSON_CONVERTER_STRINGS = {
  modeLabel: "Direction",
  modes: {
    xmlToJson: "XML → JSON",
    jsonToXml: "JSON → XML",
  },
  inputLabelXml: "XML to convert",
  inputLabelJson: "JSON to convert",
  placeholderXml: '<book id="7"><title>Dune</title></book>',
  placeholderJson: '{"book": {"@id": "7", "title": "Dune"}}',
  hint: "Converted in your browser as you type. Nothing is uploaded, and nothing is fetched, including anything a DOCTYPE points to.",
  loadExample: "Load the example",
  clear: "Clear",
  useAsInput: "Use the result as input and swap direction",
  exampleXml:
    '<?xml version="1.0" encoding="UTF-8"?>\n<catalog version="2">\n  <!-- two products -->\n  <product sku="A1">\n    <name>Pen</name>\n    <price currency="USD">1.50</price>\n    <tag>office</tag>\n    <tag>writing</tag>\n  </product>\n  <product sku="B2">\n    <name>Ink &amp; Cartridge</name>\n    <price currency="USD">4</price>\n    <discontinued/>\n  </product>\n  <note>Prices in USD</note>\n</catalog>\n',
  exampleJson:
    '{"catalog": {"@version": "2", "product": [{"@sku": "A1", "name": "Pen", "price": {"@currency": "USD", "#text": "1.50"}, "tag": ["office", "writing"]}, {"@sku": "B2", "name": "Ink & Cartridge", "price": {"@currency": "USD", "#text": "4"}, "discontinued": null}], "note": "Prices in USD"}}',
  optionsHeading: "Conventions",
  attrPrefixLabel: "Attributes are named",
  attrPrefixes: {
    "@": "@name",
    "@_": "@_name",
    _: "_name",
    "-": "-name",
  },
  textKeyLabel: "Text next to attributes or elements is stored under",
  textKeys: {
    "#text": "#text",
    _text: "_text",
    $: "$",
    text: "text",
  },
  arraysLabel: "Child elements",
  arrays: {
    repeated: "Arrays only when an element repeats",
    always: "Always arrays (the same shape every time)",
  },
  trim: "Trim text and ignore white space between elements",
  typed: "Read numbers and true/false as JSON numbers and booleans",
  emptyLabel: "An empty element <a/> becomes",
  empties: {
    null: "null",
    string: "an empty string",
  },
  includeRoot: "Keep the root element's name as the outer key",
  includeRootBack: "Use a single top-level key as the root element's name",
  jsonIndentLabel: "JSON indentation",
  jsonIndents: {
    "2": "2 spaces",
    "4": "4 spaces",
    tab: "Tab",
    compact: "Minified (one line)",
  },
  xmlIndentLabel: "XML indentation",
  xmlIndents: {
    "2": "2 spaces",
    "4": "4 spaces",
    tab: "Tab",
    compact: "Minified (one line)",
  },
  declaration: "Start with the XML declaration",
  rootNameLabel: "Root element name when one is needed",
  sampleHeading: "How these settings read an element",
  sampleXml:
    '<book id="7"><title>Dune</title><tag>sf</tag><tag>classic</tag><note lang="en">Classic</note><sale/></book>',
  results: "Result",
  outputLabelJson: "JSON",
  outputLabelXml: "XML",
  copyResult: "Copy result",
  copied: "Copied!",
  download: "Download file",
  filenameJson: "converted.json",
  filenameXml: "converted.xml",
  emptyPrompt: "Paste some XML or JSON, or load the example, to convert it.",
  valid: "Well-formed and converted.",
  rootLabel: "Root element",
  elements: "Elements",
  attributes: "Attributes",
  depth: "Levels deep",
  warningsHeading: "Worth a second look",
  problemHeading: "Could not convert",
  problemAt: "Line {line}, column {column}",
  problemNoLine: "The input could not be converted",
  messages: {
    xmlBadName:
      "A name was expected here. Names start with a letter, underscore or colon.",
    xmlUnexpectedEnd: "The XML ends in the middle of a tag.",
    xmlUnclosedElement:
      "The XML ends before <{name}> (opened on line {line}) is closed.",
    xmlMismatchedTag:
      "Found </{found}>, but the element open here is <{expected}> (opened on line {line}).",
    xmlStrayEndTag: "An end tag with no matching start tag.",
    xmlAttrQuote: "Attribute values must be in quotes.",
    xmlAttrEquals:
      "The attribute “{name}” needs an equals sign and a quoted value.",
    xmlAttrDuplicate:
      "The attribute “{name}” appears twice on the same element.",
    xmlAttrLt:
      "A less-than sign is not allowed inside an attribute value. Write &lt; instead.",
    xmlBadEntity:
      "“{text}” is not a valid reference. Write a literal ampersand as &amp;.",
    xmlUndefinedEntity:
      "The entity &{name}; is not defined. Only &lt; &gt; &amp; &quot; &apos; and numeric references are supported.",
    xmlUnclosedComment: "A comment that starts here is never closed.",
    xmlUnclosedCdata: "A CDATA section that starts here is never closed.",
    xmlUnclosedPi:
      "A processing instruction or declaration that starts here is never closed.",
    xmlMultipleRoots:
      "A document can have only one root element; this is a second one.",
    xmlNoRoot: "No root element was found.",
    xmlTextOutside: "Text is not allowed outside the root element.",
    xmlBadChar: "The character {code} is not allowed in XML.",
    xmlTooDeep: "The elements are nested more than 500 deep.",
    xmlInvalidName:
      "“{name}” (at {path}) is not a valid XML name. Names start with a letter, underscore or colon and contain no spaces.",
    xmlNestedArray:
      "A list inside a list, at {path}, cannot be written as XML elements.",
    xmlAttrNotScalar:
      "The attribute “{key}” at {path} must be a single value, not a list or an object.",
    xmlTextNotScalar: "The text value at {path} must be a single value.",
    tooLarge: "The result would be longer than {limit} characters.",
    tooDeep: "The nesting is too deep to convert.",
    internal: "Something unexpected stopped the conversion.",
    jsonUnexpectedToken: "Unexpected character “{char}”.",
    jsonUnexpectedEnd:
      "The JSON ends too early. Check for a missing closing bracket, brace or quote.",
    jsonTrailingComma:
      "A comma before a closing bracket or brace is not allowed in JSON.",
    jsonExpected: "Expected {expected} here.",
    jsonSingleQuote: "JSON strings use double quotes, not single quotes.",
    jsonUnquotedKey: "JSON keys must be in double quotes.",
    jsonComment: "JSON does not allow comments.",
    jsonLiteral: "{text} is not valid JSON. Use null, true, false or a number.",
    jsonUnterminatedString: "A string that starts here is never closed.",
    jsonControlChar:
      "A line break or control character inside a string must be written as an escape sequence.",
    jsonBadEscape:
      "Unknown escape in a string: a backslash followed by “{char}”.",
    jsonBadNumber:
      "This is not a valid JSON number (no leading zeros, trailing dot or plus sign).",
    jsonExtraData: "There is extra text after the end of the JSON value.",
    jsonTooDeep: "The JSON is nested more than 500 levels deep.",
  },
  warnings: {
    doctypeIgnored:
      "Line {line}: the DOCTYPE was skipped. Entities it declares are not expanded and nothing is fetched.",
    mixedContent:
      "Line {line}: <{name}> mixes text and elements. The text is kept, but its position among the elements is not.",
    keyCollision:
      "Line {line}: “{key}” is used both for an attribute or text and for an element, so their values were mixed.",
    duplicateKey:
      "Line {line}: the key “{key}” appears more than once; the last value is used.",
  },
} as const;

type Strings = typeof XML_TO_JSON_CONVERTER_STRINGS;
type MessageCode = keyof Strings["messages"];
type WarningCode = keyof Strings["warnings"];

const PREFIXES: readonly AttributePrefix[] = ["@", "@_", "_", "-"];
const TEXT_KEYS: readonly TextKey[] = ["#text", "_text", "$", "text"];
const INDENTS: readonly JsonIndent[] = ["2", "4", "tab", "compact"];

function describeProblem(problem: XmlProblem, strings: Strings): string {
  const template = Object.hasOwn(strings.messages, problem.code)
    ? strings.messages[problem.code as MessageCode]
    : strings.messages.internal;
  return t(template, problem.params);
}

function describeWarning(
  warning: {
    code: string;
    line: number;
    params: Readonly<Record<string, string | number>>;
  },
  strings: Strings
): string {
  const template = Object.hasOwn(strings.warnings, warning.code)
    ? strings.warnings[warning.code as WarningCode]
    : "";
  return t(template, { ...warning.params, line: warning.line });
}

export default function XmlToJsonConverterWidget() {
  const strings = useToolUi(XML_TO_JSON_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("xmlToJson");
  const [source, setSource] = useState("");
  const [options, setOptions] = useState<XmlJsonOptions>(
    DEFAULT_XML_JSON_OPTIONS
  );
  const deferred = useDeferredValue(source);
  const xmlSide = direction === "xmlToJson";
  let outcome: XmlOutcome | null = null;
  if (deferred.trim() !== "") {
    outcome = xmlSide
      ? xmlToJson(deferred, options)
      : jsonToXml(deferred, options);
  }

  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="xjc-mode"
        onChange={(value) => {
          setDirection(value);
          setSource("");
        }}
        options={[
          { value: "xmlToJson", label: strings.modes.xmlToJson },
          { value: "jsonToXml", label: strings.modes.jsonToXml },
        ]}
        value={direction}
      />
      <TextAreaField
        hint={strings.hint}
        id="xjc-input"
        label={xmlSide ? strings.inputLabelXml : strings.inputLabelJson}
        onChange={setSource}
        placeholder={xmlSide ? strings.placeholderXml : strings.placeholderJson}
        rows={12}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setSource(xmlSide ? strings.exampleXml : strings.exampleJson)
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={outcome === null || !outcome.ok}
          onClick={() => {
            if (outcome?.ok) {
              setSource(outcome.text);
              setDirection(xmlSide ? "jsonToXml" : "xmlToJson");
            }
          }}
          type="button"
        >
          {strings.useAsInput}
        </button>
      </div>
      <OptionsPanel
        onChange={setOptions}
        options={options}
        strings={strings}
        xmlSide={xmlSide}
      />
      <div aria-live="polite" className="space-y-4">
        {outcome === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results outcome={outcome} strings={strings} xmlSide={xmlSide} />
        )}
      </div>
    </section>
  );
}

function OptionsPanel({
  options,
  onChange,
  strings,
  xmlSide,
}: {
  options: XmlJsonOptions;
  onChange: (next: XmlJsonOptions) => void;
  strings: Strings;
  xmlSide: boolean;
}) {
  const set = <K extends keyof XmlJsonOptions>(
    key: K,
    value: XmlJsonOptions[K]
  ) => onChange({ ...options, [key]: value });
  const sample = xmlToJson(strings.sampleXml, {
    ...options,
    includeRoot: true,
    jsonIndent: "2",
  });
  return (
    <Panel title={strings.optionsHeading}>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="xjc-prefix"
          label={strings.attrPrefixLabel}
          onChange={(value) => set("attrPrefix", value)}
          options={PREFIXES.map((value) => ({
            value,
            label: strings.attrPrefixes[value],
          }))}
          value={options.attrPrefix}
        />
        <SelectField
          id="xjc-text-key"
          label={strings.textKeyLabel}
          onChange={(value) => set("textKey", value)}
          options={TEXT_KEYS.map((value) => ({
            value,
            label: strings.textKeys[value],
          }))}
          value={options.textKey}
        />
      </div>
      {xmlSide ? (
        <XmlSideOptions options={options} set={set} strings={strings} />
      ) : (
        <JsonSideOptions options={options} set={set} strings={strings} />
      )}
      <div className="space-y-2">
        <p className={LABEL}>{strings.sampleHeading}</p>
        <pre className={`${TEXTAREA} min-h-0 overflow-x-auto`}>
          {strings.sampleXml}
        </pre>
        <pre className={`${TEXTAREA} min-h-0 overflow-x-auto`}>
          {sample.ok ? sample.text : ""}
        </pre>
      </div>
    </Panel>
  );
}

type Setter = <K extends keyof XmlJsonOptions>(
  key: K,
  value: XmlJsonOptions[K]
) => void;

function XmlSideOptions({
  options,
  set,
  strings,
}: {
  options: XmlJsonOptions;
  set: Setter;
  strings: Strings;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="xjc-arrays"
          label={strings.arraysLabel}
          onChange={(value) => set("arrays", value)}
          options={(["repeated", "always"] as const).map((value) => ({
            value,
            label: strings.arrays[value],
          }))}
          value={options.arrays}
        />
        <SelectField
          id="xjc-empty"
          label={strings.emptyLabel}
          onChange={(value) => set("empty", value)}
          options={(["null", "string"] as const).map((value) => ({
            value,
            label: strings.empties[value],
          }))}
          value={options.empty}
        />
        <SelectField
          id="xjc-json-indent"
          label={strings.jsonIndentLabel}
          onChange={(value) => set("jsonIndent", value)}
          options={INDENTS.map((value) => ({
            value,
            label: strings.jsonIndents[value],
          }))}
          value={options.jsonIndent}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={options.trim}
          label={strings.trim}
          onChange={(value) => set("trim", value)}
        />
        <Checkbox
          checked={options.typed}
          label={strings.typed}
          onChange={(value) => set("typed", value)}
        />
        <Checkbox
          checked={options.includeRoot}
          label={strings.includeRoot}
          onChange={(value) => set("includeRoot", value)}
        />
      </div>
    </>
  );
}

function JsonSideOptions({
  options,
  set,
  strings,
}: {
  options: XmlJsonOptions;
  set: Setter;
  strings: Strings;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="xjc-xml-indent"
          label={strings.xmlIndentLabel}
          onChange={(value) => set("xmlIndent", value as XmlIndent)}
          options={INDENTS.map((value) => ({
            value,
            label: strings.xmlIndents[value],
          }))}
          value={options.xmlIndent}
        />
        <TextField
          id="xjc-root-name"
          label={strings.rootNameLabel}
          onChange={(value) => set("rootName", value)}
          value={options.rootName}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Checkbox
          checked={options.includeRoot}
          label={strings.includeRootBack}
          onChange={(value) => set("includeRoot", value)}
        />
        <Checkbox
          checked={options.declaration}
          label={strings.declaration}
          onChange={(value) => set("declaration", value)}
        />
      </div>
    </>
  );
}

function Results({
  outcome,
  strings,
  xmlSide,
}: {
  outcome: XmlOutcome;
  strings: Strings;
  xmlSide: boolean;
}) {
  const fmt = useToolNumber();
  if (!outcome.ok) {
    return <ProblemView problem={outcome.problem} strings={strings} />;
  }
  const { summary } = outcome;
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Notice>{strings.valid}</Notice>
      <StatGrid>
        <StatCard emphasis label={strings.rootLabel} value={summary.root} />
        <StatCard label={strings.elements} value={fmt(summary.elements)} />
        <StatCard label={strings.attributes} value={fmt(summary.attributes)} />
        <StatCard label={strings.depth} value={fmt(summary.depth)} />
      </StatGrid>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="xjc-output">
          {xmlSide ? strings.outputLabelJson : strings.outputLabelXml}
        </label>
        <textarea
          className={TEXTAREA}
          id="xjc-output"
          readOnly
          rows={Math.min(24, outcome.text.split("\n").length + 1)}
          spellCheck={false}
          value={outcome.text}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={outcome.text}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              xmlSide ? strings.filenameJson : strings.filenameXml,
              outcome.text,
              xmlSide ? "application/json" : "application/xml"
            )
          }
          type="button"
        >
          {strings.download}
        </button>
      </div>
      {outcome.warnings.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {outcome.warnings.slice(0, 12).map((warning) => (
              <li
                key={`${warning.code}-${warning.line}-${JSON.stringify(warning.params)}`}
              >
                {describeWarning(warning, strings)}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

function ProblemView({
  problem,
  strings,
}: {
  problem: XmlProblem;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const place =
    problem.line > 0
      ? t(strings.problemAt, {
          line: fmt(problem.line),
          column: fmt(problem.column),
        })
      : strings.problemNoLine;
  const caret = " ".repeat(Math.max(0, Math.min(problem.column - 1, 119)));
  return (
    <div className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.problemHeading}</h2>
      <ErrorNotice>
        {place} — {describeProblem(problem, strings)}
      </ErrorNotice>
      {problem.excerpt === "" ? null : (
        <pre className={`${TEXTAREA} min-h-0 overflow-x-auto`}>
          {`${problem.excerpt}\n${caret}^`}
        </pre>
      )}
      <p className={HINT}>{strings.hint}</p>
    </div>
  );
}
